<template>
  <ValidationObserver ref="observer" slim>
    <client-auth-provider>
      <template v-slot:default="{ $clientAuthData, $clientAuthMethods }">
        <div>
          <!-- Login client template -->
          <client-template-render-provider v-bind="clientTemplateProps">
            <template v-slot:default="{ $templateData }">
              <template v-if="!!$templateData.body">
                <div class="has-margin-bottom-150">
                  <u-markdown>{{ $templateData.body }}</u-markdown>
                </div>
              </template>
            </template>
          </client-template-render-provider>

          <form @submit.prevent="validateBeforeSubmit($clientAuthMethods)">
            <client-auth-login-fields
              v-model="$clientAuthData.form"
              :is-processing="isProcessing"
            />
            <b-field class="has-text-centered has-margin-top-150">
              <button
                :class="{ 'is-loading': isProcessing }"
                :disabled="isProcessing"
                :style="{ width: '15rem', maxWidth: '100%' }"
                class="button is-rounded has-theme-brand"
                type="submit"
              >
                <i18n path="_action.login" tag="strong" />
              </button>
            </b-field>
            <p class="has-text-centered">
              <router-link
                :to="{
                  name: forgottenPasswordRouteName,
                  query: { username: $clientAuthData.form.username }
                }"
                class="has-text-grey is-size-7"
              >
                <i18n path="_sentence.forgotten_password" tag="u" />
              </router-link>
            </p>
          </form>
        </div>
      </template>
    </client-auth-provider>
  </ValidationObserver>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import HasValidation from "@/mixins/HasValidation";
import type { IClientAuthProviderMethods } from "@/models/providers/clientAuth";
import { ClientTemplateSlotCodes } from "@/data/enums/templates";
import type { IState } from "@/store";
import { GuestRoutes } from "@/data/enums/router";

export default defineComponent({
  components: {
    ClientAuthProvider: () =>
      import("@/components/app/global/auth/clientAuth/clientAuthProvider.vue"),
    ClientAuthLoginFields: () =>
      import(
        "@/components/app/global/auth/clientAuth/clientAuthLoginFields.vue"
      ),
    ClientTemplateRenderProvider: () =>
      import(
        `@/components/app/global/templates/clientTemplateRenderProvider.vue`
      )
  },
  mixins: [HasValidation],
  data: () => ({
    isProcessing: false,
    forgottenPasswordRouteName: GuestRoutes.FORGOTTEN_PASSWORD
  }),
  computed: {
    clientTemplateProps() {
      return {
        code: ClientTemplateSlotCodes.LOGIN_PAGE,
        objectId: (this.$store.state as IState).brand?.id,
        withAccessToken: false
      };
    }
  },
  methods: {
    async validateBeforeSubmit(clientAuthMethods: IClientAuthProviderMethods) {
      try {
        this.isProcessing = true;
        if (!(await this.validate())) {
          throw this.$t("_sentence.form_is_not_valid");
        }
        const { isRedirecting } = await clientAuthMethods.login();
        if (isRedirecting) {
          this.isProcessing = false;
        }
      } catch (error) {
        this.isProcessing = false;
        if (error === "cancelled") {
          return;
        }
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      }
    }
  }
});
</script>
